import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoboothService, ThemeFrame } from '../../services/photobooth.service';
import { FrameAssetsManagerComponent } from '../frame-assets-manager/frame-assets-manager.component';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-theme-frames-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, FrameAssetsManagerComponent, DragDropModule],
  templateUrl: './theme-frames-editor.component.html',
  styleUrls: ['./theme-frames-editor.component.css']
})
export class ThemeFramesEditorComponent implements OnInit {
  @Input() themeId: string = '';
  @Input() isGlobal: boolean = true;
  @Input() deviceId: string = '';
  @Input() version: string | null = null;
  @Input() tileType: string = 'Theme';
  @Input() featuredFrameId?: string;

  @Output() closeEditor = new EventEmitter<void>();

  private photoboothService = inject(PhotoboothService);

  frames: ThemeFrame[] = [];
  isLoading = false;
  isModalOpen = false;
  isSaving = false;
  editingId: string | null = null;
  selectedFrameForAssets: string | null = null;

  bottomBgPreview: { name: string, url: string } | null = null;
  topBgPreview: { name: string, url: string } | null = null;
  isLoadingBackground: boolean = false;
  isUploadingBackground: boolean = false;
  isBgModalOpen: boolean = false;

  newFrame: ThemeFrame = { id: '', name: '', description: '' };
  originalId: string = '';

  ngOnInit() {
    this.loadFrames();
  }

  async loadFrames() {
    if (!this.themeId) return;

    this.isLoading = true;
    try {
      this.frames = await this.photoboothService.getFrames(this.isGlobal, this.deviceId, this.themeId, this.version);
      
      if (this.tileType === 'FeaturedFrame' && this.featuredFrameId) {
        this.frames = this.frames.filter(f => f.id === this.featuredFrameId);
      }
      
      // Auto-prompt to create the single required frame for Signature tiles if none exists
      if (this.tileType === 'Signature' && this.frames.length === 0 && !this.version) {
        this.openAddModal();
      }

      await this.loadBackground();
    } catch (error) {
      console.error('Error loading frames:', error);
      alert('Failed to load frames.');
    } finally {
      this.isLoading = false;
    }
  }

  async loadBackground() {
    this.isLoadingBackground = true;
    try {
      const response = await this.photoboothService.getFrameGroupBackground(this.isGlobal, this.deviceId, this.themeId, this.version);
      this.bottomBgPreview = response?.bottom || null;
      this.topBgPreview = response?.top || null;
    } catch (e) {
      console.error(e);
      this.bottomBgPreview = null;
      this.topBgPreview = null;
    } finally {
      this.isLoadingBackground = false;
    }
  }

  openBgModal() {
    this.isBgModalOpen = true;
  }

  closeBgModal() {
    this.isBgModalOpen = false;
  }

  async uploadBackground(event: any, layer: 'bottom' | 'top'): Promise<void> {
    const file: File = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.png')) {
      alert('Only PNG files are allowed.');
      return;
    }

    this.isUploadingBackground = true;
    try {
      const storagePath = await this.photoboothService.uploadFrameGroupBackground(this.isGlobal, this.deviceId, this.themeId, file, layer);
      await this.loadBackground();
    } catch (error) {
      console.error('Error uploading background:', error);
      alert('Failed to upload background.');
    } finally {
      this.isUploadingBackground = false;
      event.target.value = '';
    }
  }

  async removeBackground(layer: 'bottom' | 'top'): Promise<void> {
    if (!confirm(`Are you sure you want to remove the ${layer} background?`)) return;

    this.isUploadingBackground = true;
    try {
      await this.photoboothService.deleteFrameGroupBackground(this.isGlobal, this.deviceId, this.themeId, layer);
      if (layer === 'bottom') this.bottomBgPreview = null;
      if (layer === 'top') this.topBgPreview = null;
    } catch (error) {
      console.error('Error removing background:', error);
      alert('Failed to remove background.');
    } finally {
      this.isUploadingBackground = false;
    }
  }

  close() {
    this.closeEditor.emit();
  }

  openAddModal() {
    this.editingId = null;
    this.newFrame = { id: '', name: '', description: '' };
    this.isModalOpen = true;
  }

  editFrame(frame: ThemeFrame) {
    this.editingId = frame.id;
    this.originalId = frame.id;
    this.newFrame = { ...frame };
    this.isModalOpen = true;
  }

  onIdInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      input.value = input.value.trim();
      this.newFrame.id = input.value;
    }
  }

  async deleteFrame(frameId: string) {
    if (confirm('Are you sure you want to delete this frame?')) {
      try {
        await this.photoboothService.deleteFrameGroupAndAssets(this.isGlobal, this.deviceId, this.themeId, frameId);
        this.frames = this.frames.filter(f => f.id !== frameId);
      } catch (error) {
        console.error('Error deleting frame:', error);
        alert('Failed to delete frame.');
      }
    }
  }

  cancelEdit() {
    this.isModalOpen = false;
  }

  async drop(event: CdkDragDrop<ThemeFrame[]>) {
    if (this.version) return; // Read-only for versions
    
    moveItemInArray(this.frames, event.previousIndex, event.currentIndex);

    // Assign new order
    this.frames.forEach((frame, index) => {
      frame.orderIndex = index;
    });

    // Save order silently to Firebase
    try {
      const updates = this.frames.map(f => ({ id: f.id, orderIndex: f.orderIndex! }));
      await this.photoboothService.updateFrameGroupsOrder(this.isGlobal, this.deviceId, this.themeId, updates);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save the new order.');
    }
  }

  async saveFrame() {
    if (this.newFrame.id) {
      this.newFrame.id = this.newFrame.id.trim();
    }

    if (!this.newFrame.id || !this.newFrame.name) {
      alert('ID and Name are required.');
      return;
    }

    const isDuplicate = this.frames.some(f => f.id === this.newFrame.id && f.id !== this.editingId);
    if (isDuplicate) {
      alert('A frame with this ID already exists for this theme.');
      return;
    }

    this.isSaving = true;
    try {
      const oldId = (this.editingId && this.editingId !== this.newFrame.id) ? this.originalId : null;
      
      await this.photoboothService.saveFrame(
        this.isGlobal,
        this.deviceId,
        this.themeId,
        this.newFrame.id,
        this.newFrame,
        oldId
      );

      this.isModalOpen = false;
      this.loadFrames();
    } catch (error) {
      console.error('Error saving frame:', error);
      alert('Failed to save frame.');
    } finally {
      this.isSaving = false;
    }
  }

  openAssetsManager(frameId: string) {
    this.selectedFrameForAssets = frameId;
  }

  closeAssetsManager() {
    this.selectedFrameForAssets = null;
  }
}
